import { FC } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { HelpDeskColored, LiveChatColored, LockBlack, WordpressColored } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken, Text, Tooltip } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { SettingsTableColumn as Column, SettingsTableRow as Row } from "components/settings-table";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { selectHasLiveChatIntegration, selectLivechatGroups } from "store/entities/integrations";
import { ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { SettingsRoutes } from "../constants";

import { ContactFormContextMenu } from "./ContactFormContextMenu";
import { DEFAULT_LIVECHAT_CONTACT_FORM_ID } from "./constants";

interface IProps extends IContactForm {
  teams: Record<string, ITeam>;
  defaultTeamID: string;
  hasLivechatIntegration: boolean;
  onEdit(id: string, openOldBuilder?: boolean): void;
  onDelete(id: string): void;
  onDuplicate(id: string, openOldBuilder?: boolean): void;
  isAdmin: boolean;
  version?: number;
}

export const ContactFormRow: FC<IProps> = ({
  id,
  name,
  teamID,
  teams,
  defaultTeamID,
  livechatGroupIDs,
  wordpressContactForm,
  hasLivechatIntegration,
  isAdmin,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const livechatGroups = useSelector(selectLivechatGroups);
  const isLiveChatIntegrationEnabled = useSelector(selectHasLiveChatIntegration);
  const isDefault = id === DEFAULT_LIVECHAT_CONTACT_FORM_ID;
  const openOldBuilder = isDefault || livechatGroupIDs.length > 0;

  return (
    <Row>
      <Column>
        <Flex column>
          <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
            {isLiveChatIntegrationEnabled && (
              <Icon source={!openOldBuilder ? HelpDeskColored : LiveChatColored} size="small" />
            )}
            {isAdmin ? (
              <Link
                to={
                  !openOldBuilder
                    ? `${SettingsRoutes.ContactForms}/edit/${id}`
                    : `${SettingsRoutes.WidgetForms}/edit/${id}`
                }
              >
                {name}
              </Link>
            ) : (
              name
            )}
            {isDefault && (
              <Tooltip kind="invert" placement="top" triggerRenderer={<Icon source={LockBlack} size="small" />}>
                This form cannot be removed because LiveChat integration is on.
              </Tooltip>
            )}
          </Flex>
          {wordpressContactForm && (
            <Flex
              css={{
                color: withVar(DesignToken.ContentBasicSecondary),
                gap: withVar(SpacingToken.Spacing2),
              }}
            >
              <Icon source={WordpressColored} size="small" />
              Connected with WordPress
            </Flex>
          )}
        </Flex>
      </Column>
      <Column>
        {teams[teamID]?.name || <Text color="errorColor">Deleted team</Text>}
        {teamID === defaultTeamID && (
          <Text customColor={withVar(DesignToken.ContentBasicSecondary)} css={{ display: "inline" }}>
            &nbsp;(default team)
          </Text>
        )}
      </Column>
      {hasLivechatIntegration && (
        <Column>
          {isDefault && <>All unassigned groups</>}
          {!isDefault && livechatGroups && (
            <>
              {livechatGroupIDs.length === 0 ? (
                <>-</>
              ) : (
                <>
                  {livechatGroupIDs
                    .map(groupId => livechatGroups.find(group => group.id === String(groupId))?.name)
                    .filter(Boolean)
                    .join(", ")}
                </>
              )}
            </>
          )}
        </Column>
      )}
      <Column>
        <ContactFormContextMenu
          id={id}
          onEdit={onEdit}
          openOldBuilder={openOldBuilder}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          disabled={!isAdmin}
          isDefault={isDefault}
        />
      </Column>
    </Row>
  );
};
